import { forwardRef, type ButtonHTMLAttributes, type ReactNode } from 'react';
import { buttonClasses, type ButtonSize, type ButtonVariant } from '@/components/buttonClasses';

interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: ButtonVariant;
  size?: ButtonSize;
  loading?: boolean;
  /** Ícone à esquerda do texto. Enquanto carrega, o indicador de espera ocupa o lugar dele. */
  icon?: ReactNode;
}

/**
 * Botão único do sistema. `type="button"` por padrão: dentro de um `<form>` o padrão do HTML é
 * enviar o formulário, e só o botão de envio deve fazer isso (`type="submit"` explícito).
 */
export const Button = forwardRef<HTMLButtonElement, ButtonProps>(function Button(
  { variant, size, loading, icon, className, children, disabled, type = 'button', ...rest },
  ref,
) {
  return (
    <button
      ref={ref}
      type={type}
      disabled={disabled || loading}
      aria-busy={loading || undefined}
      className={buttonClasses({ variant, size, loading, className })}
      {...rest}
    >
      {loading ? (
        <svg
          className="h-4 w-4 shrink-0 animate-spin"
          viewBox="0 0 24 24"
          fill="none"
          aria-hidden="true"
        >
          <circle
            cx="12"
            cy="12"
            r="10"
            stroke="currentColor"
            strokeWidth="4"
            className="opacity-25"
          />
          <path d="M4 12a8 8 0 018-8v8z" fill="currentColor" className="opacity-75" />
        </svg>
      ) : (
        icon
      )}
      {children}
    </button>
  );
});
